import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import FormInput from "../controls/FormInput";
import { useTranslation } from "react-i18next";
import GoogleLogin from "../../components/auth/GoogleLoginButton";
import { useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { supabase } from "../../SupabaseClient";

const EmailLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [isLogin, setIsLogin] = useState(true);

  // 🌟 NOUVEAU : Mode reset password
  const [isResetMode, setIsResetMode] = useState(false);

  const { login, signup } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ---------------------------------------------------------
  // 🔐 Mot de passe oublié (réinitialisation)
  // ---------------------------------------------------------
  const handleResetPassword = async () => {
    setError("");

    if (!email) {
      setError(
        "Veuillez entrer votre courriel pour réinitialiser votre mot de passe.",
      );
      return;
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      console.error("Erreur reset password:", error.message);
      setError("Impossible d'envoyer le lien. Vérifiez votre courriel.");
    } else {
      setError("Un lien de réinitialisation vous a été envoyé !");
    }
  };

  // ---------------------------------------------------------
  // 🔐 Connexion / Inscription
  // ---------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      if (isLogin) {
        await login(email, password);
        navigate("/home");
      } else {
        if (password !== confirmPassword) {
          setError(t("auth.passwordsDontMatch"));
          return;
        }

        await signup(email, password, fullName);

        setEmail("");
        setFullName("");
        setPassword("");
        setConfirmPassword("");

        navigate("/home");
      }
    } catch (err) {
      if (err.message === "Email not confirmed") {
        setError(t("auth.emailNotConfirmed"));
      } else {
        setError(err.message || t("auth.errorOccurred"));
      }
    }
  };

  // ---------------------------------------------------------
  // 🔵 Interface
  // ---------------------------------------------------------
  return (
    <div className="w-full max-w-sm space-y-4">
      {/* Bouton fermer */}
      <div className="flex">
        <button
          onClick={() => navigate("/home")}
          className="text-primary hover:underline cursor-pointer ml-auto"
        >
          <XMarkIcon className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* TITRE */}
      {!isResetMode && (
        <h2 className="text-xl font-bold text-center">
          {isLogin ? t("auth.loginTitle") : t("auth.signupTitle")}
        </h2>
      )}

      {/* Google Login (sauf en mode reset) */}
      {!isResetMode && <GoogleLogin />}

      {!isResetMode && <div className="border-t my-6"></div>}

      {error && <p className="text-red-500">{error}</p>}

      {/* ---------------------------------------------------------------------- */}
      {/* 🌟 MODE RESET PASSWORD */}
      {/* ---------------------------------------------------------------------- */}
      {isResetMode && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-center">
            Réinitialiser le mot de passe
          </h2>

          <p className="text-gray-600 text-sm text-center">
            Entrez votre courriel et nous vous enverrons un lien pour
            réinitialiser votre mot de passe.
          </p>

          <FormInput
            label={t("auth.emailLabel")}
            name="email"
            value={email}
            onChange={(name, value) => setEmail(value)}
            placeholder={t("auth.emailPlaceholder")}
            required={true}
            type="email"
          />

          <button
            onClick={handleResetPassword}
            className="w-full bg-primary text-white py-2 px-4 rounded-full hover:bg-accent transition"
          >
            Envoyer le lien
          </button>

          <button
            onClick={() => {
              setIsResetMode(false);
              setError("");
            }}
            className="w-full text-primary underline mt-2"
          >
            Retour
          </button>
        </div>
      )}

      {/* ---------------------------------------------------------------------- */}
      {/* 🌟 MODE LOGIN / SIGNUP */}
      {/* ---------------------------------------------------------------------- */}
      {!isResetMode && (
        <form onSubmit={handleSubmit}>
          {/* Nom complet (inscription seulement) */}
          {!isLogin && (
            <FormInput
              label={t("auth.fullName")}
              name="fullName"
              value={fullName}
              onChange={(name, value) => setFullName(value)}
              placeholder={t("auth.fullNamePlaceholder")}
              required={true}
              type="text"
            />
          )}

          {/* Email */}
          <FormInput
            label={t("auth.emailLabel")}
            name="email"
            value={email}
            onChange={(name, value) => setEmail(value)}
            placeholder={t("auth.emailPlaceholder")}
            required={true}
            type="email"
          />

          {/* Mot de passe */}
          <FormInput
            label={t("auth.passwordLabel")}
            name="password"
            value={password}
            onChange={(name, value) => setPassword(value)}
            placeholder={t("auth.passwordPlaceholder")}
            required={true}
            type="password"
          />

          {/* Confirmation (inscription seulement) */}
          {!isLogin && (
            <FormInput
              label={t("auth.confirmPassword")}
              name="confirmPassword"
              value={confirmPassword}
              onChange={(name, value) => setConfirmPassword(value)}
              placeholder={t("auth.confirmPasswordPlaceholder")}
              required={true}
              type="password"
            />
          )}

          {/* Bouton principal */}
          <button
            type="submit"
            className="w-full bg-primary text-white py-2 px-4 rounded-full hover:bg-accent transition"
          >
            {isLogin ? t("auth.loginButton") : t("auth.signupButton")}
          </button>

          {/* Mot de passe oublié */}
          {isLogin && (
            <div className="mt-4 text-center">
              <span
                onClick={() => setIsResetMode(true)}
                className="text-primary hover:underline cursor-pointer"
              >
                {t("auth.forgotPassword")}
              </span>
            </div>
          )}
        </form>
      )}

      {/* Switch login/signup */}
      {!isResetMode && (
        <div className="text-sm text-gray-600 text-center">
          {isLogin ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
          <span
            onClick={() => {
              setIsLogin(!isLogin);
              setError("");
              setEmail("");
              setFullName("");
              setPassword("");
              setConfirmPassword("");
            }}
            className="text-primary hover:underline cursor-pointer"
          >
            {isLogin ? t("auth.signupLink") : t("auth.loginLink")}
          </span>
        </div>
      )}
    </div>
  );
};

export default EmailLogin;
